import {
  type DiscountDTO,
  type OldProductDTO,
  type ProductLightDTO,
  type ProductPrice
} from 'ecosystem'
import { parseFloatSafe } from './numbers'

export const getProductOriginalPrice = (product: ProductPrice): number => {
  return product.originalPrice ?? 0
}

export const getProductPrice = (product: ProductPrice): number => {
  return product.price ?? getProductOriginalPrice(product)
}

const isDiscountApplicableToProduct = (product: ProductLightDTO, discountCode: DiscountDTO) => {
  return (
    (product.brand?.id && discountCode.brandIds?.includes(product.brand.id)) ||
    discountCode.categoryIds?.some((c) => product.categoryIds?.includes(c)) ||
    discountCode.tagIds?.some((t) => product.tags?.map((pTag) => pTag.id).includes(t))
  )
}

export const getProductDiscountPrice = (
  product: ProductLightDTO,
  discountCode: DiscountDTO | null
):
  | {
      discountedPrice: number
      priceUsedForDiscount: number
    }
  | undefined => {
  if (!discountCode || !isDiscountApplicableToProduct(product, discountCode)) {
    return undefined
  }

  const discountRate = discountCode.value / 100
  const itemPrice = getProductPrice(product)
  const itemOriginalPrice = getProductOriginalPrice(product)
  const isOriginalPriceLower = itemPrice > itemOriginalPrice

  if (!discountCode.campaign && !isOriginalPriceLower) {
    const itemDiscount = itemOriginalPrice * discountRate

    return {
      priceUsedForDiscount: itemOriginalPrice,
      discountedPrice: itemOriginalPrice - itemDiscount
    }
  }

  const itemDiscount = itemPrice * discountRate

  return {
    priceUsedForDiscount: itemPrice,
    discountedPrice: itemPrice - itemDiscount
  }
}

export const getProductWeight = (fullProduct: OldProductDTO) => {
  const weightSpec = fullProduct.specifications?.find((spec) => {
    return /^(?:weight|vikt|vekt|vægt|paino|gewicht)/.test(spec.name.toLowerCase())
  })

  let weight = 0

  if (weightSpec) {
    // eslint-disable-next-line prefer-named-capture-group -- allowed only in es2018
    const unit = /\((kg|g)\)/.exec(weightSpec.name.toLowerCase())?.[1] ?? 'kg'
    weight = parseFloatSafe(weightSpec.value) ?? 0

    if (unit === 'g') {
      weight = weight / 1000
    }
  }

  return weight
}
